import { FC, MutableRefObject, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { skipToken } from '@reduxjs/toolkit/query';

import { putUtkast } from 'store/utkastSlice';

import { useAppDispatch } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaDialog } from 'raa-react';

import {
  DrawerBody,
  DrawerContainer,
  DrawerFooter,
  DrawerSection,
  NavigationDrawerHead
} from 'components/drawerSections/DrawerSections';
import ScrollToError from 'components/fieldComponents/scrollToError/ScrollToError';
import { Colors } from 'components/map/Styles';
import { penCircleSvg } from 'components/map/icons';
import { ExternalLink } from 'components/sharedElements/sharedElements';
import {
  SammansattFormValues,
  getSammansattFormSchemaBySkyddstyp
} from 'components/utkast/utkastRegistration/UtkastFormSchema';
import BeskrivningSection from 'components/utkast/utkastRegistration/sections/BeskrivningSection';
import FotografiSection from 'components/utkast/utkastRegistration/sections/FotografiSection';
import HistorikSection from 'components/utkast/utkastRegistration/sections/HistorikSection';
import ReferenserSection from 'components/utkast/utkastRegistration/sections/ReferenserSection';
import SkyddSection from 'components/utkast/utkastRegistration/sections/SkyddSection';
import TillkomstSection from 'components/utkast/utkastRegistration/sections/TillkomstSection';
import UtkastActionsSection from 'components/utkast/utkastRegistration/sections/UtkastActionsSection';

import {
  BebyggelseobjektDomaner,
  BebyggelseobjektUtkast,
  BebyggelseobjektUtkastSammansattPut,
  HandelseUtkast,
  Upphovsmaninsats,
  Upphovsmanroll,
  VinjettPutParam
} from 'models';
import { RequestStatus, RequestType } from 'models/appModels/Request';

import { DokumentationstypEnum, SkyddstypEnum } from 'enums';

import SammansattGrundinformationSection from './sections/SammansattGrundinformationSection';
import SituationsplanSection from './sections/SituationsplanSection';
import SkyddbestammelseSection from './sections/SkyddbestammelseSection';

import { useGetDokumentationQuery, usePutVinjettMutation } from 'api/endpoints/dokumentationSlice';
import { useBlockerContext } from 'context/navigationBlockContext';
import { Form, Formik, FormikHelpers } from 'formik';
import { andamalToAndamalPut } from 'utilities/convertUtkastTypes';
import { setApiValidationErrors } from 'utilities/formUtilities';
import { v4 as uuid } from 'uuid';
import { AnyObject } from 'yup';

interface SammansattUtkastFormProps {
  utkastData: BebyggelseobjektUtkast;
  domaner: BebyggelseobjektDomaner;
  requestStatus: RequestStatus;
  requestType: RequestType;
  deletedOrPublishedRef: MutableRefObject<boolean>;
}

const SammansattUtkastForm: FC<SammansattUtkastFormProps> = ({
  utkastData,
  domaner,
  requestStatus,
  requestType,
  deletedOrPublishedRef
}) => {
  const [initialValues, setInitialValues] = useState<SammansattFormValues>();
  const { id } = useParams();
  const dispatch = useAppDispatch();

  const [dirtyForm, setDirtyForm] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedVinjett, setSelectedVinjett] = useState<VinjettPutParam>();
  const [formValidationSchema, setFormValidationSchema] = useState<AnyObject>(
    getSammansattFormSchemaBySkyddstyp({
      skyddstyp: utkastData.skydd.skyddstyp,
      havt: utkastData.sammansatt?.havt_skydd || false
    })
  );

  const { setNavigationBlocker } = useBlockerContext();
  const { setDialog, unsetDialog } = useDialog();

  const { data: dokumentation, isFetching: getDokumentationIsFetching } = useGetDokumentationQuery(id ?? skipToken);

  const [putUtkastVinjett] = usePutVinjettMutation();

  const setInitialFormValues = (utkastData: BebyggelseobjektUtkast): void => {
    const formValues: SammansattFormValues = {
      anteckning: utkastData.anteckning || '',
      syfte: '',
      karaktarsdrag: utkastData.karaktarsdrag || '',
      namn: utkastData.namn || '',
      alternativnamn: [],
      ursprungligt_primar_andamal: utkastData.ursprungligt_primar_andamal
        ? andamalToAndamalPut(utkastData.ursprungligt_primar_andamal)
        : null,
      ursprungligt_sekundar_andamal: utkastData.ursprungligt_sekundar_andamal
        ? utkastData.ursprungligt_sekundar_andamal.map(andamalToAndamalPut)
        : [],
      skydd: {
        forvaltningsorganisation_id: utkastData.skydd.forvaltningsorganisation_id,
        motivering: utkastData.skydd.motivering
      },
      skyddsbestammelser: utkastData.sammansatt?.skyddsbestammelser.length
        ? utkastData.sammansatt?.skyddsbestammelser
        : (utkastData.skydd.skyddstyp === SkyddstypEnum.BYGGNADSMINNE ||
              utkastData.skydd.skyddstyp === SkyddstypEnum.STATLIGT_BYGGNADSMINNE) &&
            !utkastData.sammansatt?.havt_skydd
          ? [
              {
                attribut_id: uuid(),
                rubrik: '',
                skyddsbestammelse: ''
              }
            ]
          : [],
      beskrivning: utkastData.beskrivning || '',
      historik: utkastData.historik || '',
      geometri: utkastData.geometri,
      lokal_klassificering: utkastData.lokal_klassificering || '',
      referenser: utkastData.referenser || '',
      handelser: utkastData.handelser?.length
        ? utkastData.handelser.map((handelse: HandelseUtkast) => ({
            attribut_id: handelse.attribut_id,
            handelsetyp: handelse.handelsetyp,
            kommentar: handelse.kommentar,
            datering_kand: String(handelse.datering_kand),
            datering_from: handelse.datering_from ? String(handelse.datering_from) : '',
            datering_tom: handelse.datering_tom ? String(handelse.datering_tom) : '',
            upphovsman: handelse.upphovsmanroller.map((upphovsmaninsats: Upphovsmaninsats) => ({
              id: upphovsmaninsats.upphovsman.upphovsman_id,
              roller: upphovsmaninsats.roller.map((upphovsmanroll: Upphovsmanroll) => upphovsmanroll.rolltyp)
            }))
          }))
        : []
    };
    setInitialValues(formValues);
  };

  useEffect(() => {
    if (utkastData && domaner) {
      setInitialFormValues(utkastData);
    }
  }, [utkastData, domaner]);

  useEffect(() => {
    setNavigationBlocker({
      message: 'Formuläret har ändringar som inte har sparats. Vill du fortsätta utan att spara?',
      blocked: !submitting && dirtyForm,
      cancelText: 'avbryt',
      proceedText: 'fortsätt'
    });
  }, [dirtyForm, setNavigationBlocker, submitting]);

  const onSelectVinjett = async (utkastId: string, vinjettParam: VinjettPutParam) => {
    setSelectedVinjett(vinjettParam);
    await putUtkastVinjett({ utkastId, vinjettParam });
  };

  const handleSubmit = async (
    values: SammansattFormValues,
    { setFieldError, setSubmitting }: FormikHelpers<SammansattFormValues>
  ): Promise<void> => {
    if (!id) {
      throw new Error('Fick inget id för utkast!');
    }

    const sammansattUtkastPut: BebyggelseobjektUtkastSammansattPut = {
      beskrivning: values.beskrivning,
      anteckning: values.anteckning || '',
      historik: values.historik,
      handelser: values.handelser.map((formValue) => ({
        attribut_id: formValue.attribut_id,
        handelsetyp: formValue.handelsetyp,
        datering_from: formValue.datering_from ? Number(formValue.datering_from) : null,
        datering_tom: formValue.datering_tom ? Number(formValue.datering_tom) : null,
        datering_kand: formValue.datering_kand === 'true',
        kommentar: formValue.kommentar,
        upphovsman: formValue.upphovsman
      })),
      namn: values.namn || '',
      skydd: {
        motivering: values.skydd.motivering,
        forvaltningsorganisation_id: values.skydd.forvaltningsorganisation_id || null
      },
      ursprungligt_primar_andamal: values.ursprungligt_primar_andamal,
      ursprungligt_sekundar_andamal: values.ursprungligt_sekundar_andamal,
      geometri: values.geometri,
      referenser: values.referenser,
      lokal_klassificering: values.lokal_klassificering,
      skyddsbestammelser: values.skyddsbestammelser
    };

    try {
      await dispatch(putUtkast({ utkastId: id, utkast: sammansattUtkastPut })).unwrap();
    } catch (error: any) {
      setApiValidationErrors(error, setFieldError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {initialValues && (
        <Formik
          initialValues={initialValues}
          validationSchema={formValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
          innerRef={(ref) => {
            setSubmitting(ref?.isSubmitting || false);
            setDirtyForm(ref?.dirty || false);
          }}
        >
          {() => (
            <Form id="utkastForm" className="tw-h-full">
              <DrawerContainer>
                <NavigationDrawerHead />
                <DrawerBody>
                  <DrawerSection>
                    <div className="tw-mb-2 tw-flex tw-text-raa-green">
                      <img
                        src={`data:image/svg+xml,${encodeURIComponent(penCircleSvg(Colors.GREEN))}`}
                        alt="Ikon för ej publicerat utkast"
                        className="tw-mr-2 tw-h-6"
                      />
                      <span className="tw-grow">Utkast {<>({utkastData.aktivitetref?.aktivitetsnamn})</>}</span>
                      <ExternalLink
                        href={`${location.pathname.replace('/redigera', '')}`}
                        text="Förhandsvisa"
                        beforeNavigation={
                          dirtyForm
                            ? () => {
                                setDialog(RaaDialog, {
                                  header: 'Du har osparade ändringar',
                                  close: unsetDialog,
                                  cancel: unsetDialog,
                                  cancelButtonText: 'stäng',
                                  ariaDescribedBy: 'dialog-description',
                                  children: (
                                    <p id="dialog-description" className="tw-mt-8">
                                      Spara dina ändringar för att förhandsvisa
                                    </p>
                                  )
                                });
                              }
                            : undefined
                        }
                      />
                    </div>
                    <h1 className="tw-heading-lg">{utkastData.titel}</h1>
                    <p className="tw-mb-4 tw-mt-2 tw-font-semibold">
                      OBS! I detta formulär registrerar du byggnadsminnet som helhet. Den ingående bebyggelsen
                      registreras för sig.
                    </p>
                  </DrawerSection>

                  <SammansattGrundinformationSection utkastData={utkastData} domaner={domaner} />
                  <TillkomstSection />
                  <SkyddSection utkastData={utkastData} domaner={domaner} />
                  {!utkastData.sammansatt?.havt_skydd && <SkyddbestammelseSection utkastData={utkastData} />}
                  <BeskrivningSection utkastData={utkastData} domaner={domaner} showEgenskaper={false} />
                  <HistorikSection />

                  <SituationsplanSection
                    utkast={utkastData}
                    dokumentation={
                      dokumentation?.filter((d) => d.dokumentationstyp === DokumentationstypEnum.RITNING) ?? []
                    }
                    getDokumentationIsFetching={getDokumentationIsFetching}
                    selectedVinjett={selectedVinjett}
                    onSelectVinjett={onSelectVinjett}
                  />
                  <FotografiSection
                    dokumentation={
                      dokumentation?.filter((d) => d.dokumentationstyp === DokumentationstypEnum.BILD) ?? []
                    }
                    getDokumentationIsFetching={getDokumentationIsFetching}
                    utkast={utkastData}
                    selectedVinjett={selectedVinjett}
                    onSelectVinjett={onSelectVinjett}
                  />
                  <ReferenserSection />
                </DrawerBody>
                <DrawerFooter>
                  <UtkastActionsSection
                    utkastData={utkastData}
                    requestStatus={requestStatus}
                    requestType={requestType}
                    setFormValidationSchema={setFormValidationSchema}
                    sparaFormSchema={getSammansattFormSchemaBySkyddstyp({
                      skyddstyp: utkastData.skydd.skyddstyp,
                      havt: utkastData.sammansatt?.havt_skydd || false
                    })}
                    publiceraFormSchema={getSammansattFormSchemaBySkyddstyp({
                      skyddstyp: utkastData.skydd.skyddstyp,
                      havt: utkastData.sammansatt?.havt_skydd || false
                    })}
                    deletedOrPublishedRef={deletedOrPublishedRef}
                  />
                </DrawerFooter>
              </DrawerContainer>
              <ScrollToError />
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default SammansattUtkastForm;
